import { css } from '@styled-system/css';
import { Clock, Euro, Gauge, Layers, Package, Percent, SlidersHorizontal, TrendingUp } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterValues } from '@/features/analysisPageNiches/types';
import { Button } from '@/shared/components/ui/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Input } from '@/shared/components/ui/Input';
import { Popover } from '@/shared/components/ui/Popover';

// ============================================================================
// Filtres locaux du tableau des niches — seuils de MÉTRIQUES (% de vente,
// score, prix moyen…) : ils masquent des lignes, ils ne changent pas les
// chiffres du périmètre (ça, c'est la barre de segment).
// Remplace l'ancienne NicheFilterModal v1 (antd) par un popover DS.
// ============================================================================

const content = css({
  display: 'flex',
  flexDirection: 'column',
  width: '320px',
});

const fieldRow = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2.5',
  paddingY: '4',
  // Séparation légère entre les champs pour guider la lecture
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  _first: { paddingTop: '0' },
  _last: { borderBottomWidth: '0', paddingBottom: '0' },
});

const fieldLabel = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2.5',
  fontSize: 'caption',
  color: 'text.secondary',
  fontWeight: 'medium',
});

const fieldIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
});

const rangeInputs = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
});

const rangeSeparator = css({
  color: 'text.tertiary',
  fontSize: 'caption',
});

const inputStyle = css({ width: '120px' });

const unitStyle = css({
  fontSize: 'caption',
  color: 'text.tertiary',
});

const footerRow = css({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '4',
});

const clearButton = css({
  background: 'transparent',
  border: 'none',
  fontSize: 'caption',
  fontFamily: 'sans',
  color: 'text.tertiary',
  cursor: 'pointer',
  padding: '1',
  _hover: { color: 'text.primary', textDecoration: 'underline' },
});

const parseNumber = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

interface RangeFieldProps {
  icon: ReactNode;
  label: string;
  /** Unité affichée dans les champs (€, %, h…). */
  unit?: string;
  min: number | undefined;
  max: number | undefined;
  minPlaceholder: string;
  maxPlaceholder: string;
  onChange: (min: number | undefined, max: number | undefined) => void;
}

const RangeField: FC<RangeFieldProps> = ({ icon, label, unit, min, max, minPlaceholder, maxPlaceholder, onChange }) => (
  <div className={fieldRow}>
    <span className={fieldLabel}>
      <span className={fieldIcon}>{icon}</span>
      {label}
    </span>
    <div className={rangeInputs}>
      <Input
        type="number"
        inputSize="sm"
        min={0}
        value={min ?? ''}
        onChange={(event) => onChange(parseNumber(event.target.value), max)}
        placeholder={minPlaceholder}
        trailing={unit ? <span className={unitStyle}>{unit}</span> : undefined}
        className={inputStyle}
      />
      <span className={rangeSeparator}>—</span>
      <Input
        type="number"
        inputSize="sm"
        min={0}
        value={max ?? ''}
        onChange={(event) => onChange(min, parseNumber(event.target.value))}
        placeholder={maxPlaceholder}
        trailing={unit ? <span className={unitStyle}>{unit}</span> : undefined}
        className={inputStyle}
      />
    </div>
  </div>
);

export interface NicheFilterPopoverProps {
  filters: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
  /** false : pas de filtre score (analyses vendeur). */
  showScore?: boolean;
}

export const NicheFilterPopover: FC<NicheFilterPopoverProps> = ({ filters, onFilterChange, showScore = true }) => {
  const { t } = useTranslation();

  const hasFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined,
  );

  const handleNicheTypeChange = (type: 'brand' | 'category', checked: boolean) => {
    const currentTypes = filters.nicheTypes ?? [];
    const newTypes = checked ? [...currentTypes, type] : currentTypes.filter((item) => item !== type);
    onFilterChange({ ...filters, nicheTypes: newTypes.length > 0 ? newTypes : undefined });
  };

  const iconProps = { size: 13, strokeWidth: 1.75 } as const;

  return (
    <Popover
      placement="bottom-start"
      trigger={
        <Button variant="secondary" size="md" icon={<SlidersHorizontal size={13} strokeWidth={1.75} />}>
          {t('niches.filters')}
        </Button>
      }
    >
      <div className={content}>
        <RangeField
          icon={<Percent {...iconProps} />}
          label={t('niches.colSoldPct')}
          unit="%"
          min={filters.soldPctMin}
          max={filters.soldPctMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(soldPctMin, soldPctMax) => onFilterChange({ ...filters, soldPctMin, soldPctMax })}
        />

        {showScore && (
          <RangeField
            icon={<Gauge {...iconProps} />}
            label={`${t('niches.colScore')} /100`}
            min={filters.scoreMin}
            max={filters.scoreMax}
            minPlaceholder={t('segment.min')}
            maxPlaceholder={t('segment.max')}
            onChange={(scoreMin, scoreMax) => onFilterChange({ ...filters, scoreMin, scoreMax })}
          />
        )}

        <RangeField
          icon={<Euro {...iconProps} />}
          label={t('niches.colAvgPrice')}
          unit="€"
          min={filters.avgPriceMin}
          max={filters.avgPriceMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(avgPriceMin, avgPriceMax) => onFilterChange({ ...filters, avgPriceMin, avgPriceMax })}
        />

        <RangeField
          icon={<TrendingUp {...iconProps} />}
          label={t('niches.colSoldPerDay')}
          min={filters.soldPerDayMin}
          max={filters.soldPerDayMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(soldPerDayMin, soldPerDayMax) => onFilterChange({ ...filters, soldPerDayMin, soldPerDayMax })}
        />

        <RangeField
          icon={<Clock {...iconProps} />}
          label={t('niches.colTimeToSell')}
          unit="h"
          min={filters.timeToSellMin}
          max={filters.timeToSellMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(timeToSellMin, timeToSellMax) => onFilterChange({ ...filters, timeToSellMin, timeToSellMax })}
        />

        <RangeField
          icon={<Package {...iconProps} />}
          label={t('niches.colProducts')}
          min={filters.productCountMin}
          max={filters.productCountMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(productCountMin, productCountMax) =>
            onFilterChange({ ...filters, productCountMin, productCountMax })
          }
        />

        <div className={fieldRow}>
          <span className={fieldLabel}>
            <span className={fieldIcon}>
              <Layers {...iconProps} />
            </span>
            {t('niches.typeFilter')}
          </span>
          <div className={css({ display: 'flex', gap: '6' })}>
            <Checkbox
              checked={filters.nicheTypes?.includes('brand') ?? false}
              onChange={(checked) => handleNicheTypeChange('brand', checked)}
              label={t('niches.typeBrand')}
            />
            <Checkbox
              checked={filters.nicheTypes?.includes('category') ?? false}
              onChange={(checked) => handleNicheTypeChange('category', checked)}
              label={t('niches.typeCategory')}
            />
          </div>
        </div>

        {hasFilters && (
          <div className={footerRow}>
            <button type="button" className={clearButton} onClick={() => onFilterChange({})}>
              {t('niches.clearAll')}
            </button>
          </div>
        )}
      </div>
    </Popover>
  );
};
